import React, { useEffect, useRef, useState } from "react";
import { Box, Divider, Typography } from "@material-ui/core";
import { CustomSelect } from "../../../../../../CustomSelect";
import { CustomInput } from "../../../../../../CustomInput";
import { PhoneNumberInput } from "../../../../../../PhoneNumberInput";
import { EmailInput } from "../../../../../../EmailInput";
import { checkDuplication } from "../../../../../../../../Contexts/ContactValidationContext/helpers";
import { useDebouncedAction } from "../../../../../../../../Hooks/DebouncedAction";
import { CustomAutocomplete } from "../../../../../../CustomAutocomplete";
import { MyLeadsTypesEnum } from "../../../../../../../../Enums";
import { checkIfAgentIsBlocked } from "../../../../../../../../Services";
import { useVerticalNav } from "../../../../../../../../Contexts/VerticalNavContext";
import { AlertCircle } from "../../../../../../../../assets/icons";
import { useSelectedTheme } from "../../../../../../../../Hooks";
import { CustomSwitch } from "../../../../../../CustomSwitch";

// styles
import useStyles from "./styles";

const MyLeadContactDetailsFields = ({
  data,
  state,
  selected,
  isQuickAdd,
  onStateChangeHandler,
  onSelectedChangeHandler,
  onSearchUsers,
  translate,
  errors,
  setErrors,
}) => {
  const styles = useStyles();
  const searchTimer = useRef(null);
  const {
    theme: { palette },
  } = useSelectedTheme();
  const { setAlertBoxContent } = useVerticalNav();
  const [isAgentBlockedDialogOpen, setIsAgentBlockedDialogOpen] =
    useState(false);

  const checkDuplicateFields = useDebouncedAction(async ({ value }) => {
    setErrors((prev) => ({ ...prev, checkingEmail: true }));

    const exists = await checkDuplication(value);
    if (exists) {
      setErrors((prev) => ({ ...prev, isEmailExist: true }));
    } else {
      setErrors((prev) => ({ ...prev, isEmailExist: false }));
    }
    setErrors((prev) => ({ ...prev, checkingEmail: false }));
  }, {delay:1000});

  useEffect(() => {
    if (state.isForAutoRotation === true || state.isSmartRotation) {
      onStateChangeHandler("referredTo", null);
      onSelectedChangeHandler("referredTo", null);
    }
  }, [state.isForAutoRotation, state.isSmartRotation]);

  const checkAgentIsBlocked = async (value) => {
  if(value){
    try {
      const res = await checkIfAgentIsBlocked(value?.id);
      return res;
    } catch {
      setAlertBoxContent({
        display: true,
        variant: "error",
        title: translate("an-error-blocked-agent"),
        onClose: () => {
          setAlertBoxContent(null);
        },
      });
    }
  }
  };
  const changeRealatedFiled = (newValue) => {
    if (newValue == 1 || newValue == 2) {
      onStateChangeHandler("priceFrom", null); 
      onStateChangeHandler("priceTo", null);
      onStateChangeHandler("areaFrom", null);
      onStateChangeHandler("areaTo", null);
    } else {
      onStateChangeHandler("priceFrom", 10000);
      onStateChangeHandler("priceTo", 60000000);
      onStateChangeHandler("areaFrom", 200); 
      onStateChangeHandler("areaTo", 10000); 
    }
  };

  return (
    <Box className={styles.wrapperMyLeadContactDetailsFields}>
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Salutation")}*
        </Typography>
        <CustomSelect
          placeholder={translate("Select")}
          style={{ width: "100%" }}
          options={data.salutation}
          defaultValue={selected?.salutation}
          getOptionLabel={(option) => option.lookupItemName}
          getOptionValue={(option) => option.lookupItemId}
          onValueChange={(newValue) => {
            onStateChangeHandler("salutationId", newValue);
            onSelectedChangeHandler("salutation", newValue);
          }}
        />
      </Box>
      <Divider />
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("First-name")}*
        </Typography>
        <CustomInput
          placeholder={"Enter"}
          type="text"
          value={state.firstName || ""}
          hasSearchIcon={false}
          errorMessage={errors.firstNameError}
          hasError={errors.firstNameError}
          submitted={errors.firstNameError}
          onChange={(event) => {
            onStateChangeHandler("firstName", event.target.value);
          }}
          inputContainerOverrideStyles={styles.customInput}
        />
      </Box>
      <Divider />
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Last-name")}*
        </Typography>
        <CustomInput
          placeholder={"Enter"}
          type="text"
          value={state.lastName || ""}
          errorMessage={errors.lastNameError}
          hasError={errors.lastNameError}
          submitted={errors.lastNameError}
          hasSearchIcon={false}
          onChange={(event) => {
            onStateChangeHandler("lastName", event.target.value);
          }}
          inputContainerOverrideStyles={styles.customInput}
        />
      </Box>
      <Divider />
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Phone-number")}*
        </Typography>
        <PhoneNumberInput
          Style={{ width: "100%" }}
          name="phoneNumber-ref"
          idRef="phoneNumberRef"
          inputPlaceholder="+1 (555) 000-0000"
          value={selected?.mobileNumbers || null}
          country="ae"
          onInputChanged={(value) => {
            onStateChangeHandler("mobileNumbers", [value] || null);
            onSelectedChangeHandler("mobileNumbers", value || null);
          }}
          error={errors.phoneError}
        />
      </Box>
      <Divider />
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Email")}
        </Typography>
        <EmailInput
          idRef={"email-ref"}
          isLoading={errors.checkingEmail}
          isDisabled={errors.checkingEmail}
          value={state.emailAddresses}
          helperText={errors.emailError}
          error={errors.emailError}
          onInputBlur={(e) => {
            const currentValue = e.target.value;
            if (currentValue && !errors.emailError) {
              checkDuplicateFields({ value: currentValue });
            }
          }}
          inputPlaceholder="Enter email"
          onInputChanged={(e) => {
            const value = e.target.value;
            setErrors((prev) => ({
              ...prev,
              isEmailExist: false,
              emailError: false,
            }));
            onStateChangeHandler("emailAddresses", [value]);
          }}
        />
      </Box>
      <Divider />
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Language")}*
        </Typography>
        <CustomSelect
          placeholder={translate("Select")}
          style={{ width: "100%" }}
          options={data.language}
          defaultValue={selected?.language}
          getOptionLabel={(option) => option.lookupItemName}
          getOptionValue={(option) => option.lookupItemId}
          onValueChange={(newValue) => {
            onStateChangeHandler("languageId", newValue);
            onSelectedChangeHandler("language", newValue);
          }}
        />
      </Box>
      <Divider />

      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Nationality")}*
        </Typography>
        <CustomAutocomplete
          placeholder={translate("Select")}
          options={data.nationality || []}
          onChange={(e, newValue) => {
            onSelectedChangeHandler("nationality", newValue);
            onStateChangeHandler(
              "nationalityId",
              newValue && newValue.lookupItemId
            );
          }}
          value={selected.nationality || null}
          getOptionLabel={(option) => option?.lookupItemName || ""}
          getOptionValue={(option) => option?.lookupItemId || ""}
          renderOption={(option) => option?.lookupItemName || ""}
        />
      </Box>
      <Divider />

      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Contact-class")}*
        </Typography>
        <CustomAutocomplete
          placeholder={translate("Select")}
          options={(data && data.contactClass) || []}
          onChange={(e, newValue) => {
            onSelectedChangeHandler("contactClass", newValue);
            onStateChangeHandler(
              "contactClassId",
              (newValue && newValue.lookupItemId) || null
            );
          }}
          value={selected && selected.contactClass}
          getOptionLabel={(option) => option?.lookupItemName || ""}
          getOptionValue={(option) => option?.lookupItemId || ""}
          renderOption={(option) => option?.lookupItemName || ""}
        />
      </Box>
      <Divider />
      {isQuickAdd && (
        <>
          <Box className={styles.containerItem}>
            <Typography className={styles.containerTitle}>
              {translate("Lead-class")}*
            </Typography>
            <CustomSelect
              placeholder={translate("Select")}
              style={{ width: "100%" }}
              options={MyLeadsTypesEnum}
              defaultValue={selected?.leadClass}
              getOptionLabel={(option) => option.value}
              getOptionValue={(option) => option.id}
              onValueChange={(newValue) => {
                onStateChangeHandler("edit", {
                  ...state,
                  leadClassId: newValue,
                  referredTo: null,
                  unitTypeId: null,
                });
                changeRealatedFiled(newValue);
                onSelectedChangeHandler("edit", {
                  ...selected,
                  referredTo: null,
                  unitType: null,
                  leadClass: newValue,
                });
              }}
            />
          </Box>
          <Divider />
        </>
      )}

      {state && state.leadClassId && (
        <>
          <Box className={styles.containerItem}>
            <Typography className={styles.containerTitle}>
              {translate("Referred-To")}
            </Typography>
            <Box className={styles.containerReferedTo}>
              <CustomAutocomplete
                placeholder={translate("Select")}
                options={(data && data.referredToUsers) || []}
                onChange={async (event, newValue) => {
                  setIsAgentBlockedDialogOpen(false);
                  if (await checkAgentIsBlocked(newValue)) {
                    setIsAgentBlockedDialogOpen(true);
                    onSelectedChangeHandler("referredTo", null);
                    onStateChangeHandler("referredTo", null);
                    return;
                  }
                  onSelectedChangeHandler("referredTo", newValue);
                  onStateChangeHandler("referredTo", newValue && newValue.id);
                }}
                isDisabled={state.isForAutoRotation || state.isSmartRotation}
                value={selected.referredTo}
                getOptionLabel={(option) => option.fullName}
                getOptionValue={(option) => option?.id || ""}
                renderOption={(option) => option.fullName}
                onInputKeyUp={(event) => {
                  setIsAgentBlockedDialogOpen(false);
                  const { value } = event.target;
                  if (searchTimer.current) clearTimeout(searchTimer.current);
                  searchTimer.current = setTimeout(() => {
                    onSearchUsers(value, "referredToUsers");
                  }, 700);
                }}
              />
              {isAgentBlockedDialogOpen && (
                <Box className={styles.containerAlertAgent}>
                  <Box className={styles.wrapperIcon}>
                    <Box className={styles.containerIcon}>
                      <AlertCircle
                        width="20"
                        height="20"
                        fill={palette.icons.lightFgError}
                      />
                    </Box>
                  </Box>
                  <Box className={styles.containerDescription}>
                    <Box className={styles.titleAlert}>
                      {translate("Agent-blocked")}
                    </Box>
                    <Box className={styles.subTitleAlert}>
                      {translate(
                        "The-selected-agent-is-currently-blocked-and-cannot-be-assigned-Please-choose-a-different-agent"
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
          <Divider />
        </>
      )}
      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Referred-by")}
        </Typography>
        <CustomAutocomplete
          placeholder={translate("Select")}
          options={data.referredByUsers}
          onChange={(e, newValue) => {
            onSelectedChangeHandler("referredBy", newValue);
            onStateChangeHandler("referredBy", newValue && newValue.id);
          }}
          value={selected.referredBy}
          getOptionLabel={(option) => option.fullName}
          getOptionValue={(option) => option?.id || ""}
          renderOption={(option) => option.fullName}
          onInputKeyUp={(event) => {
            const { value } = event.target;
            if (searchTimer.current) clearTimeout(searchTimer.current);
            searchTimer.current = setTimeout(() => {
              onSearchUsers(value);
            }, 700);
          }}
        />
      </Box>
      <Divider />

      <Box className={styles.containerItem}>
        <Typography className={styles.containerTitle}>
          {translate("Send-to-rotation")}
        </Typography>

        <CustomSwitch
          size={"md"}
          checked={state.isForAutoRotation}
          onChange={(e) => {
            setIsAgentBlockedDialogOpen(false);
            onStateChangeHandler("isForAutoRotation", !state.isForAutoRotation);
          }}
          isDisabled={false}
        />
      </Box>
      {(state?.leadClassId == 3 || state?.leadClassId == 4) && <Divider />}

      {(state?.leadClassId == 3 || state?.leadClassId == 4) && (
        <>
          <Box className={styles.containerItem}>
            <Typography className={styles.containerTitle}>
              {translate("Smart-Assignment")}
            </Typography>
            <CustomSwitch
              size={"md"}
              checked={state.isSmartRotation}
              onChange={(e) => {
                setIsAgentBlockedDialogOpen(false);
                onStateChangeHandler("isSmartRotation", !state.isSmartRotation);
              }}
              isDisabled={false}
            />
          </Box>
        </>
      )}
    </Box>
  );
};
export default MyLeadContactDetailsFields;
